<template>
    <!-- `chat-message-text` class is used as a selector in the devops repo, for synthetic checks -->
    <div ref="targetDiv" class="chat-message-text" :data-message-component-id="componentId" data-testid="chat-message-text">
        <!-- During streaming, render as single block (original behavior) for performance -->
        <!-- eslint-disable-next-line vue/no-v-html - html content sanitized below -->
        <div v-if="isStreaming" v-html="streamingContent" />
        <!-- After streaming, segment into copyable blocks -->
        <template v-for="(segment, index) in contentSegments" v-else :key="index">
            <!-- eslint-disable-next-line vue/no-v-html - html content sanitized below -->
            <div v-if="segment.type === 'markdown'" v-html="segment.html" />
            <CopyableBlock v-else :content="segment.content" :html="segment.html" :block-index="segment.blockIndex" :message-id="messageId" />
        </template>
    </div>
    <!-- Stands in for the email draft block hidden from the prose until it closes and the card renders -->
    <ChatLoadingFrame v-if="isDraftingEmail" class="w-fit" :is-loading="true" data-testid="email-draft-pending">Drafting email</ChatLoadingFrame>
    <!-- XML related components -->
    <ChatLoadingFrame v-if="showGeneratingSummary" class="w-fit" :is-loading="showGeneratingSummary">Generating Feedback Summary</ChatLoadingFrame>
    <!-- Feedback related components -->
    <FeedbackRequestQuestion v-if="hasFeedbackQuestion" :question="feedbackQuestion" :message-id="messageId" />
    <FeedbackResponseSummary v-if="rawFeedbackResponseSummary" :feedback-summary="rawFeedbackResponseSummary" class="md:-ml-12.5 md:w-[calc(100%+90px)]!" />
    <FeedbackSummaryReport v-if="showFeedbackReport" :feedback-data="feedbackReport" class="md:-ml-12.5 md:w-[calc(100%+90px)]!" />
</template>

<script>
// https://stackoverflow.com/questions/69951687/vue-3-defineprops-are-referencing-locally-declared-variables
// locally declared variables used in a compiler macro like defineProps need to be module scoped
const MODE = {
    WORDS: "words",
    CHARACTERS: "characters",
};
</script>

<script setup>
import escape from "lodash/escape";
import { marked } from "marked";
import { computed, inject, onMounted, ref, useId, watch } from "vue";

import { ACTION, pluckMultipleActions } from "./chatActions";
import ChatLoadingFrame from "./ChatLoadingFrame.vue";
import CopyableBlock from "./components/CopyableBlock.vue";
import FeedbackRequestQuestion from "./Feedback/FeedbackRequestQuestion.vue";
import FeedbackResponseSummary from "./Feedback/FeedbackResponseSummary.vue";
import FeedbackSummaryReport from "./Feedback/FeedbackSummaryReport.vue";
import { useConnectionPopovers } from "./useConnectionPopovers";
import { noImageRenderer, sanitizeForChat } from "./utils/markdown";

const { message, messageId, animate, rate, mode, messageObj } = defineProps({
    message: {
        type: String,
        default: "",
    },
    messageId: {
        type: Number,
        required: false,
    },
    // Whether to apply rendering effect.
    animate: {
        type: Boolean,
        default: false,
    },
    // Rate in milliseconds in which text is rendered, given the `mode`.
    rate: {
        type: Number,
        default: undefined,
        required: false,
    },
    // Mode in which text is rendered
    mode: {
        type: String,
        default: MODE.WORDS,
        validator(value) {
            return [MODE.WORDS, MODE.CHARACTERS].includes(value);
        },
    },
    messageObj: {
        type: Object,
        default: undefined,
    },
});

const componentId = useId();

// Matches content between opening and closing "---" delimiters (both required)
const COPYABLE_BLOCK_REGEX = /^---[ \t]*\n([\s\S]+?)\n---[ \t]*$/gm;
// Minimum character length for a block to be treated as copyable.
// Filters out trivial matches like empty blocks, punctuation-only content (e.g., "---\n...\n---"),
// or accidental delimiter patterns (e.g., "---\nThanks!\n---").
const MIN_COPYABLE_BLOCK_LENGTH = 10;

// Mirrors EMAIL_DRAFT_OPENING_TAG in coach_core/coach_core/chat/email_drafts.py, which owns the
// contract prompted in ai_templates/global_vars/core_universal_paths.yaml.
const EMAIL_DRAFT_OPENING_TAG = "<email_draft>";
const EMAIL_DRAFT_CLOSING_TAG = "</email_draft>";

const showFeedbackReport = ref(false);
const feedbackReport = inject("feedbackReport");
const cursorPosition = ref(0);
const running = ref(false);

// XML related
const xmlTags = ref([]);
const xmlTagPattern = /<([^>]+)>([\s\S]*?)<\/\1>/g;
const feedbackPattern = /<finalized_feedback_summary>([\s\S]*?)<\/finalized_feedback_summary>/g;

// Feedback related
const hasFeedbackQuestion = computed(() => {
    return xmlTags.value.some((tag) => tag.tag === "finalized_feedback_question");
});

const feedbackQuestion = computed(() => {
    const questionTag = xmlTags.value.find((tag) => tag.tag === "finalized_feedback_question");
    return questionTag ? questionTag.content.trim() : "";
});

const showGeneratingSummary = computed(() => {
    const slice = animate ? message.slice(0, cursorPosition.value) : message;
    const openingTagIndex = slice.indexOf("<finalized_feedback_summary>");
    if (openingTagIndex === -1) {
        return false;
    }

    // Check if there's a matching closing tag after this opening tag
    const remainingSlice = slice.substring(openingTagIndex);
    const closingTagIndex = remainingSlice.indexOf("</finalized_feedback_summary>");

    return closingTagIndex === -1;
});

const rawFeedbackResponseSummary = computed(() => {
    const slice = animate ? message.slice(0, cursorPosition.value) : message;

    const match = feedbackPattern.exec(slice);
    if (match && match[1]) {
        // Create a new match object with trimmed content
        const trimmedMatch = [...match];
        trimmedMatch[1] = match[1].trim();
        return trimmedMatch;
    }
    return match;
});

// Protocols that must never appear in link hrefs. We neutralize these here, in
// addition to DOMPurify, so link safety never depends solely on the sanitizer.
const DANGEROUS_LINK_PROTOCOLS = ["javascript:", "vbscript:", "data:"];

const renderers = {
    ...noImageRenderer,
    link({ href, text }) {
        // Block dangerous protocols (javascript:, vbscript:, data:)
        const normalizedHref = href?.trim().toLowerCase();
        const isDangerous = DANGEROUS_LINK_PROTOCOLS.some((protocol) => normalizedHref?.startsWith(protocol));
        const safeHref = isDangerous ? "#" : href;
        return `<a target="_blank" href="${escape(safeHref)}" rel="nofollow noopener noreferrer">${escape(text)}</a>`;
    },
    // Escape raw HTML instead of rendering it - prevents XSS from raw tags like <script>, <img onerror>, etc.
    html({ text }) {
        return escape(text);
    },
};

const hooks = ref(new marked.Hooks());

const rawSlice = computed(() => (animate ? message.slice(0, cursorPosition.value) : message));

// Index of an email draft block that has opened but not yet closed, or -1 when there isn't one.
const openEmailDraftIndex = (slice) => {
    const index = slice.lastIndexOf(EMAIL_DRAFT_OPENING_TAG);
    return index !== -1 && !slice.includes(EMAIL_DRAFT_CLOSING_TAG, index) ? index : -1;
};

// Only Nadia's drafts become cards, so a user who types the markup keeps seeing what they wrote.
const openAssistantEmailDraftIndex = (slice) => (messageObj?.role === "user" ? -1 : openEmailDraftIndex(slice));

// Simpler regex-based approach
const cleanedSlice = computed(() => {
    let slice = rawSlice.value;

    // Remove complete feedback blocks
    slice = slice.replace(feedbackPattern, "");
    // Handle incomplete/streaming feedback blocks
    // If we find an opening tag without a closing tag, remove everything from the opening tag onwards
    const openingTagIndex = slice.indexOf("<finalized_feedback_summary>");
    if (openingTagIndex !== -1) {
        // Check if there's a matching closing tag after this opening tag
        const remainingSlice = slice.substring(openingTagIndex);
        const closingTagIndex = remainingSlice.indexOf("</finalized_feedback_summary>");

        if (closingTagIndex === -1) {
            // No closing tag found, so this is an incomplete block - remove from opening tag onwards
            slice = slice.substring(0, openingTagIndex);
        }
    }

    // A half-streamed email draft has no closing tag yet, so the generic tag stripping below leaves it
    // in the prose. Hide it until the block closes and the parsed `email_draft` line renders the card;
    // `isDraftingEmail` stands in for the hidden region so the message doesn't look stalled.
    const draftOpeningTagIndex = openAssistantEmailDraftIndex(slice);
    if (draftOpeningTagIndex !== -1) {
        slice = slice.substring(0, draftOpeningTagIndex);
    }

    // Your existing XML tag logic for other tags
    if (xmlTagPattern.test(slice)) {
        slice = slice.replace(xmlTagPattern, (match, tagName, content) => {
            const existingTag = xmlTags.value.find((tag) => tag.tag === tagName);
            let trimmedContent = content.trim();

            if (existingTag) {
                existingTag.content = trimmedContent;
                // Make an exception for the `feedback_show_report` tag, since it is expected to be empty, and just triggers some logic to retrieve and show the finalized report
            } else if (trimmedContent.length > 0 || tagName === "feedback_show_report") {
                xmlTags.value.push({ tag: tagName, content: trimmedContent });
            }
            return "";
        });
    }

    return slice;
});

const parseMarkdown = (markdown) => marked.parse(markdown, { hooks: hooks.value, extensions: { renderers } });

const parseAndSanitizeMarkdown = (markdown) => {
    return sanitizeForChat(parseMarkdown(markdown));
};

// During streaming (no messageId yet) or animation, render as single markdown block
const isStreaming = computed(() => !messageId || (animate && running.value));
const streamingContent = computed(() => parseMarkdown(cleanedSlice.value));

// The prose is truncated at an unclosed draft block, so a long email body would otherwise leave the
// message looking stalled mid-sentence until the card appears. Only while streaming: a finished
// message with an unclosed tag is a cut-off generation, not work in progress.
const isDraftingEmail = computed(() => isStreaming.value && openAssistantEmailDraftIndex(rawSlice.value) !== -1);

// After streaming completes, segment into copyable blocks (limited to first block for simplicity)
const contentSegments = computed(() => {
    if (isStreaming.value) {
        return [];
    }
    const slice = cleanedSlice.value;
    const segments = [];
    let lastIndex = 0;

    for (const match of slice.matchAll(COPYABLE_BLOCK_REGEX)) {
        const content = match[1].trim();
        if (content.length < MIN_COPYABLE_BLOCK_LENGTH) {
            continue;
        }

        if (match.index > lastIndex) {
            segments.push({ type: "markdown", html: parseAndSanitizeMarkdown(slice.slice(lastIndex, match.index)) });
        }
        // Using <hr> tags directly; markdown "---" wasn't rendering reliably (interpreted as heading underline)
        segments.push({ type: "copyable", content, html: "<hr>" + parseAndSanitizeMarkdown(content) + "<hr>", blockIndex: 0 });
        lastIndex = match.index + match[0].length;
        break; // Only process first valid copyable block
    }

    if (lastIndex < slice.length) {
        segments.push({ type: "markdown", html: parseAndSanitizeMarkdown(slice.slice(lastIndex)) });
    }

    return segments;
});

const connectionChatMessageActions = computed(() => pluckMultipleActions([messageObj ?? { lines: [] }], [ACTION.TAGGED_PERSON]));

useConnectionPopovers(hooks, componentId, connectionChatMessageActions);

onMounted(() => {
    if (!animate) {
        return;
    }

    running.value = true;
});

watch(
    () => message,
    (value) => {
        /*
         * Resume rendering of a partial if it had been rendered completely but a
         * new segment of the message was received since.
         */
        if (animate && !running.value && value.length > cursorPosition.value) {
            running.value = true;
        }
    },
);

watch(
    xmlTags,
    (value) => {
        if (value.some((tag) => tag.tag === "feedback_show_report")) {
            showFeedbackReport.value = true;
        }
    },
    { deep: true, immediate: true },
);

watch(running, (value, prev) => {
    if (!prev && value) {
        run();
    }
});

const run = () => {
    switch (mode) {
        case MODE.CHARACTERS:
            splitByCharacter();
            return;
        case MODE.WORDS:
            splitByWord();
            return;
    }
};

const splitByCharacter = () => {
    cursorPosition.value++;
    loop(splitByCharacter);
};

const splitByWord = () => {
    const segment = message.slice(cursorPosition.value);
    const nextIndex = segment.indexOf(" ");

    if (nextIndex === -1) {
        cursorPosition.value = message.length;
    } else {
        cursorPosition.value = cursorPosition.value + nextIndex + 1;
    }

    loop(splitByWord);
};

const loop = (next) => {
    /*
     * Stop looping if we've reached the end of the message. The method
     * in watch() will resume the loop if a new chunk is received.
     */
    if (cursorPosition.value >= message.length) {
        running.value = false;
        return;
    }

    /*
     * If no rate is specified, we defer the looping to
     * `requestAnimationFrame`, which is scheduled by the browser and
     * typically runs at around ~14ms.
     */
    if (rate) {
        window.setTimeout(next, rate);
    } else {
        window.requestAnimationFrame(next);
    }
};
</script>

<style scoped>
.chat-message-text :deep() {
    pre {
        /*
             * Markdown code fences may cause container to overflow.
             */
        width: 100%;
        white-space: pre-line;
        overflow-x: auto;
    }
}
</style>
